import { useHandler } from "@redotech/react-util/hook";
import XIcon from "@redotech/redo-icons/x_filled.svg";
import {
  type ConditionGroup,
  type RuleCondition,
  type RuleConditionsStep,
} from "@redotech/redo-model/advanced-flow/rules/schemas";
import {
  DateQueryOperator,
  RuleConditionType,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo } from "react";
import { ConditionGroupEditor } from "../../../../rules/components/condition-group-editor";
import { useAvailableSteps } from "../hooks/use-available-steps";
import type {
  DeepPartial,
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../index";

/**
 * Type guard to check if an item is a condition group
 */
export function isConditionGroup(
  item: DeepPartial<ConditionGroup> | DeepPartial<RuleCondition>,
): item is DeepPartial<ConditionGroup> {
  return "operator" in item && item.operator !== undefined;
}

/**
 * Count total leaf conditions recursively
 */
export function countConditionsRecursive(
  group: DeepPartial<ConditionGroup>,
): number {
  if (!group.conditions) {
    return 0;
  }

  return group.conditions.reduce((count, item) => {
    if (isConditionGroup(item)) {
      return count + countConditionsRecursive(item);
    } else {
      return count + 1;
    }
  }, 0);
}

/**
 * Check if a single condition is valid (has required fields populated)
 */
function isConditionValid(condition: DeepPartial<RuleCondition>): boolean {
  if (!condition.conditionType) {
    return false;
  }

  switch (condition.conditionType) {
    case RuleConditionType.MESSAGE_SENTIMENT:
      return (
        Array.isArray(condition.sentiments) && condition.sentiments.length > 0
      );
    case RuleConditionType.TICKET_CHANNEL:
      return Array.isArray(condition.channels) && condition.channels.length > 0;
    case RuleConditionType.TOPIC:
      return Array.isArray(condition.topics) && condition.topics.length > 0;
    case RuleConditionType.AFTER_HOURS:
      return true;
    case RuleConditionType.ASSIGNMENT_TYPE:
      return condition.assignmentType !== undefined;
    case RuleConditionType.LAST_ORDER_TIME: {
      if (!condition.queryOperator) {
        return false;
      }
      switch (condition.queryOperator) {
        case DateQueryOperator.WITHIN_LAST:
          return condition.days !== undefined && condition.days > 0;
        case DateQueryOperator.ON_OR_BEFORE:
        case DateQueryOperator.ON_OR_AFTER:
          return condition.date !== undefined;
        case DateQueryOperator.BETWEEN:
          return (
            condition.startDate !== undefined && condition.endDate !== undefined
          );
        case DateQueryOperator.NEVER:
          return true;
        default:
          return true;
      }
    }
    case RuleConditionType.SENT_FROM_EMAIL:
    case RuleConditionType.RECEIVED_AT_EMAIL:
    case RuleConditionType.SUBJECT_LINE:
    case RuleConditionType.MESSAGE_BODY_MATCHES:
      return true;
    default:
      return true;
  }
}

/**
 * Check if all conditions in a group are valid recursively
 */
function areAllConditionsValid(group: DeepPartial<ConditionGroup>): boolean {
  if (!group.conditions) {
    return true;
  }

  return group.conditions.every((item) => {
    if (isConditionGroup(item)) {
      return areAllConditionsValid(item);
    } else {
      return isConditionValid(item);
    }
  });
}

const RuleConditionsInspector = memo(function RuleConditionsInspector({
  step,
  steps,
  setStep,
  onClose,
  setHoveredNodeId,
}: StepTypeDetailsProps<PartialWithRequiredType<RuleConditionsStep>>) {
  const handleUpdateGroup = useHandler((updated: ConditionGroup) => {
    setStep({ ...step, conditionGroups: updated });
  });

  const rootGroup = (step.conditionGroups || {
    operator: "AND",
    conditions: [],
  }) as ConditionGroup;

  const trueStep = step.nextTrueId
    ? steps.find((s) => s.id === step.nextTrueId)
    : undefined;
  const falseStep = step.nextFalseId
    ? steps.find((s) => s.id === step.nextFalseId)
    : undefined;

  const { availableSteps, createConnectionHandler, onItemHover } =
    useAvailableSteps(step, steps, setStep, setHoveredNodeId);

  const handleTrueStepSelected = createConnectionHandler(0);
  const handleFalseStepSelected = createConnectionHandler(1);

  return (
    <Flex dir="column" gap="xl" justify="flex-start">
      <Flex justify="space-between" w="full">
        <Text fontSize="lg" fontWeight="semibold">
          Rule Conditions
        </Text>
        {onClose && (
          <div className={inspectorStyles.closeButton} onClick={onClose}>
            <XIcon height="16" width="16" />
          </div>
        )}
      </Flex>

      <Text
        fontSize="sm"
        style={{ color: "var(--redo-colors-text-secondary)" }}
      >
        Add conditions and groups with AND/OR logic for flexible rule matching.
      </Text>

      <Flex dir="column" gap="lg">
        <ConditionGroupEditor group={rootGroup} onChange={handleUpdateGroup} />

        {(!rootGroup.conditions || rootGroup.conditions.length === 0) && (
          <Flex className={inspectorStyles.emptyState} dir="column" gap="sm">
            <Text
              fontSize="sm"
              style={{ color: "var(--redo-colors-text-tertiary)" }}
            >
              No conditions added yet. At least one condition is required.
            </Text>
          </Flex>
        )}
      </Flex>

      <Divider />

      <Flex dir="column" gap="lg">
        <Text fontSize="md" fontWeight="semibold">
          Next Steps
        </Text>

        <Flex dir="column" gap="md">
          <RedoSingleSelectDropdownInput<string>
            fitToAnchor
            label="If conditions match (True)"
            onOptionMouseEnter={(item) => onItemHover(item.value)}
            onOptionMouseLeave={() => onItemHover(null)}
            options={availableSteps}
            optionSelected={({ item }) => {
              handleTrueStepSelected(item.value);
            }}
            placeholder="Select a step or choose Unassign"
            selectedItem={
              trueStep
                ? availableSteps.find((opt) => opt.value === trueStep.id)
                : undefined
            }
          />
        </Flex>

        <Flex dir="column" gap="md">
          <RedoSingleSelectDropdownInput<string>
            fitToAnchor
            label="If conditions do not match (False)"
            onOptionMouseEnter={(item) => onItemHover(item.value)}
            onOptionMouseLeave={() => onItemHover(null)}
            options={availableSteps}
            optionSelected={({ item }) => {
              handleFalseStepSelected(item.value);
            }}
            placeholder="Select a step or choose Unassign"
            selectedItem={
              falseStep
                ? availableSteps.find((opt) => opt.value === falseStep.id)
                : undefined
            }
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

export const ruleConditionsStepType: FrontendStepType<
  PartialWithRequiredType<RuleConditionsStep>
> = {
  valid: (state) => {
    const hasConditions =
      state.conditionGroups !== undefined &&
      countConditionsRecursive(state.conditionGroups) > 0;
    const hasRouting =
      state.nextTrueId !== undefined || state.nextFalseId !== undefined;
    const allConditionsValid =
      state.conditionGroups !== undefined &&
      areAllConditionsValid(state.conditionGroups);
    return hasConditions && hasRouting && allConditionsValid;
  },
  type: "custom",
  customInspector: RuleConditionsInspector,
  updateConnection: (state, handleIndex, targetStepId) => {
    return produce(state, (draft) => {
      if (handleIndex === 0) {
        draft.nextTrueId = targetStepId;
      } else if (handleIndex === 1) {
        draft.nextFalseId = targetStepId;
      } else {
        throw new Error(
          `Invalid handle index ${handleIndex} for Rule Conditions step`,
        );
      }
    });
  },
  removeConnection: (state, targetStepId) => {
    return produce(state, (draft) => {
      if (draft.nextTrueId === targetStepId) {
        draft.nextTrueId = undefined;
      }
      if (draft.nextFalseId === targetStepId) {
        draft.nextFalseId = undefined;
      }
    });
  },
};
